import React, { useEffect, useRef, useState } from 'react';
import DataTablePagination from '../../DataTablePagination';
import { Search } from 'lucide-react';
import { can_visit, getPractice, notify } from '../../../helpers';
import { useDocumentTitle } from '../../../hooks/customDocumentTitleHook';
import { deleteUser, fetchAllUsers, fetchRoles, updateUserRole } from '../../../services/users';
import AllMembersDataTable from '../../DataTable/AllMembersDataTable';

export default function Members({selectedPracticeChange,fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }
  const [usersList, setUsersList] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const isFetched = useRef(false);

  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    location_id: location_id,
    account_id: account_id,
    search_term: "",
    order_by: [],
  });

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  useEffect(() => {
    if (!isFetched.current) {
      loadAllRoles();
      isFetched.current = true;
    }
  }, []);

  const loadAllRoles = async () => {
    try {
      const response = await fetchRoles();
      if (response?.data) {
        const roleNames = response?.data?.filter((role) => role.status === true).map((role) => role.role_name);
        setUserRoles(roleNames);
      } else {
        setUserRoles([]);
      }
    } catch (error) {
      console.error("Error fetching loadAllRoles data:", error);
    }
  };

  const loadAllUsers = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const response = await fetchAllUsers( filter, account_id, location_id );
      const updatedData = response.data?.results?.map((row) => ({
        ...row,
        checked: false,
      }));
      setUsersList(updatedData);
      setPaginationCount({
        totalCount: response.data.count,
        totalPages: response.data.total_pages,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        notify("error", 'Something went wrong');
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const handleChangeSearchTerm = (event) => {
    setFilter({ ...filter, search_term: event.target.value });
    setDataLoading(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  const handleUpdatedUserRole = async (data, handleClose = () => {}) => {
    const filteredData = data?.roles?.filter((input) =>userRoles?.includes(input));
    data.roles = filteredData

    try {
      if(can_visit("settings_users_all_users_edit")){
        console.log(data);
        var response = await updateUserRole(data, account_id, location_id);
        if (response?.detail) {
          notify("error", response?.detail);
        } else if(response?.data?.status === 'success'){
          await loadAllUsers(filter);
          notify("success", response?.data?.message);
        } else {
          notify("error",  response?.data?.message);
        }
      }
      handleClose();
    } catch (error) {
      notify("error", "Something went wrong");
    }
  };

  const handleDeleteRecord = async (id) => {
    try {
      if(can_visit("settings_users_all_users_delete")){
        var response = await deleteUser(id, account_id, location_id);
        if (response?.detail) {
          notify("error", response?.detail);
        } else if(response?.data?.status === 'success'){
          await loadAllUsers(filter);
          notify("success", response?.data?.message);
        } else {
          notify("error",  response?.data?.message);
        }
      }
    } catch (error) {
      notify("error", "Something went wrong");
    }
  };

  useEffect(() => {
    loadAllUsers(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  useDocumentTitle("All Members");

  return (
    <div className="docpro_data_table_wrap">
      <div className="docpro_data_table_headings">
        <h3 style={{ marginBottom: '0', fontSize: '16px' }}>All Members</h3>
        <form className="docpro_search_form width_100">
          <input
            type="text"
            placeholder="Search"
            className="docpro_search_input"
              onChange={handleChangeSearchTerm}
          />
          <button className="docpro_search_btn">
            <Search width={20} height={20} />
          </button>
        </form>
      </div>

      <AllMembersDataTable
        tableData={usersList || []}
        handleDeleteRecord={handleDeleteRecord}
        reloadData={loadAllUsers}
        reloadDataFilter={filter}
        dataLoading={dataLoading}
        setDataLoading={setDataLoading}
        handleUpdatedUserRole={handleUpdatedUserRole}
        userRoles={userRoles}
      />
      {(usersList.length > 0 && !dataLoading) && (
        <DataTablePagination
          currentPage={filter.page}
          totalPages={paginationCount.totalPages}
          onPageChange={handlePageChange}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          totalRecords={paginationCount.totalCount}
          currentPageData={filter.per_page}
        />
      )}
    </div>
  );
}